import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import DashboardInstallBanner from "../components/DashboardInstallBanner.jsx";
import {
  BookOpen, Pill, ShieldAlert, Calculator, Sparkles, Star, Clock,
  Search, ArrowRight, TrendingUp, AlertCircle, Baby, Syringe, Zap, FileText
} from "lucide-react";

const quickActions = [
  { to: "/app/protocols", icon: BookOpen, label: "Protocols", color: "bg-[#EFF4FF] text-[#111827]" },
  { to: "/app/drugs", icon: Pill, label: "Drugs", color: "bg-emerald-50 text-emerald-700" },
  { to: "/app/paediatric-drugs", icon: Baby, label: "Paed Doses", color: "bg-pink-50 text-pink-700" },
  { to: "/app/interactions", icon: ShieldAlert, label: "Interactions", color: "bg-rose-50 text-rose-700" },
  { to: "/app/antibiotic-guide", icon: Syringe, label: "Antibiotics", color: "bg-teal-50 text-teal-700" },
  { to: "/app/calculators", icon: Calculator, label: "Calculators", color: "bg-amber-50 text-amber-700" },
  { to: "/app/quick-reference", icon: Zap, label: "Quick Ref", color: "bg-orange-50 text-orange-700" },
  { to: "/app/icd10", icon: FileText, label: "ICD-10", color: "bg-blue-50 text-blue-700" },
  { to: "/app/favorites", icon: Star, label: "Favorites", color: "bg-yellow-50 text-yellow-700" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [recent, setRecent] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/recent").then((r) => setRecent(r.data)).catch(() => {});
    api.get("/protocols").then((r) => setProtocols(r.data.slice(0, 6)));
  }, []);

  const isPremium = user && (user.role === "admin" || user.subscription_status === "active" || user.subscription_status === "trial");

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Welcome back</div>
          <h1 className="font-heading text-3xl font-bold text-[#111827] mt-1">
            Hello, {user?.full_name?.split(" ")[0]}
          </h1>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (q.trim()) window.location.href = `/app/search?q=${encodeURIComponent(q)}`;
          }}
          className="relative w-full md:w-96"
        >
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search protocols, drugs, calculators..."
            className="pl-9 h-11 bg-white"
            data-testid="dashboard-search"
          />
        </form>
      </div>

      <DashboardInstallBanner />

      {/* Subscription banner */}
      {user?.subscription_status === "free" && user?.role !== "admin" && (
        <Card className={user?.has_used_trial ? "border-rose-300 bg-rose-50" : "border-[#0B5FFF]/30 bg-[#EFF4FF]/40"} data-testid={user?.has_used_trial ? "trial-ended-banner" : "free-banner"}>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-md text-white flex items-center justify-center flex-shrink-0 ${user?.has_used_trial ? "bg-rose-600" : "bg-[#0B5FFF]"}`}>
                {user?.has_used_trial ? <AlertCircle className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
              </div>
              <div>
                <div className={`font-semibold ${user?.has_used_trial ? "text-rose-900" : "text-[#111827]"}`}>
                  {user?.has_used_trial
                    ? "Your free trial has ended — Premium access is locked"
                    : "You're on the Free plan — 50 emergency protocols"}
                </div>
                <div className={`text-sm ${user?.has_used_trial ? "text-rose-800/80" : "text-slate-700"}`}>
                  {user?.has_used_trial
                    ? "Choose Monthly (R89) or Annual (R899) to restore the full 500+ library, drugs, calculators and ICD-10 search."
                    : "Upgrade for the full 500+ protocol library, drugs, calculators, ICD-10 search and more."}
                </div>
              </div>
            </div>
            <Link to="/pricing">
              <Button className={user?.has_used_trial ? "bg-rose-600 hover:bg-rose-700" : "bg-[#0B5FFF] hover:bg-[#0A54E0]"} data-testid="upgrade-btn">
                {user?.has_used_trial ? "Upgrade now" : "See pricing"}
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {quickActions.map(({ to, icon: Icon, label, color }) => (
          <Link
            key={to}
            to={to}
            className="group bg-white border border-border rounded-2xl p-4 shadow-soft hover:shadow-premium hover:-translate-y-1 transition-all duration-200"
            data-testid={`quick-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center mb-3 transition-transform duration-200 group-hover:scale-105`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="font-heading font-semibold text-[#111827] text-sm">{label}</div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent */}
        <Card className="border-slate-200 lg:col-span-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-[#111827] flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" /> Recently viewed
              </h3>
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-slate-500">No recent items yet. Browse protocols or drugs to start.</p>
            ) : (
              <ul className="space-y-2">
                {recent.slice(0, 8).map((r) => (
                  <li key={`${r.item_type}-${r.item_id}`}>
                    <Link
                      to={r.item_type === "calculator" ? `/app/calculators/${r.item_id}` : `/app/${r.item_type}s/${r.item_id}`}
                      className="flex items-center justify-between gap-3 p-2 rounded hover:bg-slate-50 text-sm"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Badge className="bg-slate-100 text-slate-700 border-0 capitalize text-[10px] flex-shrink-0">{r.item_type}</Badge>
                        <span className="truncate">{r.title}</span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Featured protocols */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-[#111827] flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-500" /> Featured protocols
              </h3>
              <Link to="/app/protocols" className="text-sm text-[#111827] font-medium hover:underline" data-testid="view-all-protocols">
                View all →
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {protocols.map((p) => (
                <Link
                  key={p.protocol_id}
                  to={`/app/protocols/${p.protocol_id}`}
                  className="block p-4 border border-slate-200 rounded-md hover:border-[#0B5FFF]/40 hover:shadow-sm"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`text-[10px] border-0 ${
                      p.severity === "critical" ? "bg-rose-100 text-rose-700" :
                      p.severity === "high" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {p.severity}
                    </Badge>
                    <span className="text-xs text-slate-500 capitalize">{(p.category || "").replace(/_/g, " ")}</span>
                  </div>
                  <div className="font-heading font-semibold text-[#111827] leading-snug text-sm">{p.title}</div>
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{p.definition}</p>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
